import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiZap, FiX } = FiIcons;

export default function AIButton({ isOpen, onClick, accentColor, themeColors }) {
  const bg = themeColors?.aiButtonBg || themeColors?.aiIcon || accentColor;
  const text = themeColors?.aiButtonText || '#ffffff';

  return (
    <div className="fixed bottom-20 left-4 sm:bottom-6 sm:left-6 z-[45]" data-ai>
      <motion.button
        onClick={onClick}
        whileTap={{ scale: 0.92 }}
        className="relative flex items-center justify-center w-11 h-11 sm:w-[52px] sm:h-[52px]"
        title="AI Assistant"
      >
        {/* Outer glow ring */}
        {!isOpen && (
          <>
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ background: `radial-gradient(circle, ${bg}55 0%, transparent 70%)` }}
              animate={{ scale: [1, 1.6, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ background: `radial-gradient(circle, ${bg}33 0%, transparent 70%)` }}
              animate={{ scale: [1, 2, 1], opacity: [0.3, 0, 0.3] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
            />
          </>
        )}

        {/* Main button */}
        <motion.div
          animate={isOpen ? { rotate: 0 } : { rotate: [0, -8, 8, 0] }}
          transition={isOpen ? {} : { duration: 4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 2 }}
          className="relative w-full h-full rounded-2xl flex items-center justify-center shadow-2xl overflow-hidden"
          style={{
            background: isOpen
              ? `linear-gradient(135deg, #333 0%, #111 100%)`
              : `linear-gradient(135deg, ${bg} 0%, ${bg}bb 100%)`,
          }}
        >
          {/* Shimmer overlay */}
          {!isOpen && (
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ background: `linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%)` }}
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 1.5, ease: 'easeInOut' }}
            />
          )}

          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div key="close"
                initial={{ rotate: -90, opacity: 0, scale: 0.5 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: 90, opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.2 }}>
                <SafeIcon icon={FiX} className="w-5 h-5" style={{ color: '#aaa' }} />
              </motion.div>
            ) : (
              <motion.div key="open"
                initial={{ rotate: 90, opacity: 0, scale: 0.5 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: -90, opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.2 }}>
                <SafeIcon icon={FiZap} className="w-5 h-5" style={{ color: text }} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Label badge */}
        <AnimatePresence>
          {!isOpen && (
            <motion.div
              initial={{ opacity: 0, x: -6, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -6, scale: 0.8 }}
              transition={{ delay: 0.5 }}
              className="hidden sm:block absolute left-14 whitespace-nowrap px-2.5 py-1 rounded-xl text-[10px] font-bold tracking-wide pointer-events-none"
              style={{ backgroundColor: bg, color: text, boxShadow: `0 2px 12px ${bg}55` }}
            >
              AI
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}